// First-touch campaign tags (utm_*) for the current browser session, saved on the
// application so we can see which ad brought it. Campaign tags only, no identifiers,
// and sessionStorage only, so it ends with the session.
const KEY = "ctribe_attribution";
export const ATTRIBUTION_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export type Attribution = Partial<Record<(typeof ATTRIBUTION_FIELDS)[number], string>> & { landingPath: string; capturedAt: string };

export function captureAttribution() {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const tags = Object.fromEntries(
      ATTRIBUTION_FIELDS.filter((f) => params.get(f)).map((f) => [f, params.get(f)!.slice(0, 100)])
    );
    if (Object.keys(tags).length === 0) return;
    const value: Attribution = { ...tags, landingPath: window.location.pathname.slice(0, 200), capturedAt: new Date().toISOString() };
    window.sessionStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Storage blocked: attribution is optional.
  }
}

export function readAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

/** Campaign tags for a checkout request body (CLabs saves them on the Stripe session). */
export function checkoutAttribution(): Partial<Record<(typeof ATTRIBUTION_FIELDS)[number] | "landingPath", string>> | undefined {
  const a = readAttribution();
  if (!a) return undefined;
  const out: Record<string, string> = {};
  for (const f of ATTRIBUTION_FIELDS) if (a[f]) out[f] = a[f]!;
  if (a.landingPath) out.landingPath = a.landingPath;
  return out;
}

/**
 * Server side: whitelist and trim campaign tags from a request body into Stripe
 * metadata keys (utm_source, ..., landing_path). Anything else is dropped.
 */
export function attributionMetadata(input: unknown): Record<string, string> {
  if (!input || typeof input !== "object") return {};
  const src = input as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const f of ATTRIBUTION_FIELDS) {
    const v = src[f];
    if (typeof v === "string" && v.trim()) out[f] = v.trim().slice(0, 100);
  }
  if (typeof src.landingPath === "string" && src.landingPath) out.landing_path = src.landingPath.slice(0, 200);
  return out;
}
